import { VOICE_LOCAL_CODES } from "../../interfaces/VoiceObject";

export class VoiceApiError extends Error {
  readonly code: string;
  readonly detail?: string;
  readonly retryable?: boolean;
  readonly requestId?: string;
  readonly httpStatus?: number;

  constructor(
    code: string,
    message: string,
    options: {
      detail?: string;
      retryable?: boolean;
      requestId?: string;
      httpStatus?: number;
    } = {},
  ) {
    super(message);
    this.name = "VoiceApiError";
    this.code = code;
    Object.assign(this, options);
  }
}

export function isAbortError(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    error.name === "AbortError"
  );
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}

/** Shared envelope handling for every voice skill, including gateway/non-JSON errors. */
export async function readVoiceResponse<T>(
  response: Response,
  valid: (value: unknown) => value is T,
  allContents = false,
): Promise<T> {
  let body: Record<string, unknown> | undefined;
  try {
    body = record(await response.json());
  } catch (error) {
    if (isAbortError(error)) throw error;
  }
  const info = record(
    Array.isArray(body?.errorInformation)
      ? body.errorInformation[0]
      : undefined,
  );
  const result: unknown = Array.isArray(body?.contents)
    ? allContents
      ? body.contents
      : body.contents[0]
    : undefined;
  if (response.ok && !info && valid(result)) return result;

  const details = record(info?.additionalDetails);
  const requestId =
    typeof details?.requestId === "string"
      ? details.requestId
      : (response.headers.get("X-Voice-Request-Id") ?? undefined);
  throw new VoiceApiError(
    String(
      info?.errorCode ??
        (response.ok
          ? VOICE_LOCAL_CODES.BAD_RESPONSE
          : `HTTP_${response.status}`),
    ),
    "Voice request failed",
    {
      detail:
        typeof info?.errorDetail === "string" ? info.errorDetail : undefined,
      retryable:
        typeof details?.retryable === "boolean"
          ? details.retryable
          : response.ok || response.status >= 500 || response.status === 429,
      requestId,
      httpStatus: response.status,
    },
  );
}

export function voiceAudioFileName(mimeType: string): string {
  switch (mimeType.split(";")[0].trim().toLowerCase()) {
    case "audio/mp4":
    case "audio/x-m4a":
    case "audio/aac":
      return "voice.m4a";
    case "audio/ogg":
      return "voice.ogg";
    case "audio/wav":
    case "audio/x-wav":
      return "voice.wav";
    case "audio/mpeg":
      return "voice.mp3";
    default:
      return "voice.webm";
  }
}
